<script>
  import { 
    onMount, 
    tick 
  } from "svelte";

  let {
    items = [],
    height = '100%',
    item_height = null,
    children = undefined,
    buffer_size = 1
  } = $props();

  // read-only, but visible to consumers via bind:start
  let start = $state(0);
  let end = $state(0);

  // local state
  let height_map = $state([]);
  let rows = $state([]);
  let viewport = $state();
  let contents = $state();
  let viewport_height = $state(0);
  let mounted = $state(false);

  let top = $state(0);
  let bottom = $state(0);
  let average_height = $state();
  
  const visible = $derived.by(() => {
    const buffer_start = Math.max(0, start - buffer_size);
    const buffer_end = Math.min(items.length, end + buffer_size);
    
    return items.slice(buffer_start, buffer_end).map((data, i) => {
      return { index: buffer_start + i, data };
    });
  });


  // whenever `items` changes, invalidate the current heightmap
  $effect(() => {
    if (!mounted) return;

    const observer = new ResizeObserver(() => {
      if (viewport) refresh(items, viewport_height, item_height);
    });
      
    observer.observe(viewport);
    return () => observer.disconnect();
  });

  async function refresh(
    items,
    viewport_height,
    item_height
  ) {
    const { scrollTop } = viewport;

    await tick(); // wait until the DOM is up to date

    // Set initial end value if it's 0
    if (end === 0) {
      end = Math.min(items.length, Math.ceil(viewport_height / (item_height || 50)));
    }

    let content_height = top - scrollTop;
    let i = start;

    while (content_height < viewport_height && i < items.length) {
      let row = rows[i - start];

      if (!row) {
        end = i + 1;
        await tick(); // render the newly visible row
        row = rows[i - start];
      }

      const item_ = (height_map[i] =
        item_height || row?.offsetHeight || 50);
      content_height += item_;
      i += 1;
    }

    end = i;

    const remaining = items.length - end;
    average_height = (top + content_height) / end;

    if (end === 0) {
      average_height = 0;
    }

    bottom = remaining * average_height;
    height_map.length = items.length;
    if (top >= bottom) {
      // scroll only if we are outside the viewbox
      viewport.scrollTo(0, 0);
    }
  }

  // Debounce scroll handler
  let scroll_timeout;
  async function handle_scroll() {
    if (scroll_timeout) return;
    
    scroll_timeout = setTimeout(async () => {
      const { scrollTop } = viewport;

      const old_start = start;

      for (let v = 0; v < rows.length; v += 1) {
        height_map[start + v] =
          item_height || rows[v].offsetHeight;
      }

      let i = 0;
      let y = 0;

      const threshold = average_height / 2;
      while (i < items.length) {
        const item_ = height_map[i] || average_height;
        if (y + item_ > scrollTop - threshold) {
          start = i;
          top = y;
          break;
        }
        y += item_;
        i += 1;
      }

      while (i < items.length) {
        y += height_map[i] || average_height;
        i += 1;

        if (y > scrollTop + viewport_height + threshold) break;
      }

      end = Math.min(i + 1, items.length);

      const remaining = items.length - end;
      average_height = y / end;

      while (i < items.length) height_map[i++] = average_height;
      bottom = remaining * average_height;

      // prevent jumping if we scrolled up into unknown territory
      if (start < old_start) {
        await tick();

        let expected_height = 0;
        let actual_height = 0;

        for (let i = start; i < old_start; i += 1) {
          if (rows[i - start]) {
            expected_height += height_map[i];
            actual_height +=
              item_height || rows[i - start].offsetHeight;
          }
        }

        const d = actual_height - expected_height;
        viewport.scrollTo(0, scrollTop + d);
      }

      scroll_timeout = null;
    }, 16); // roughly one frame at 60fps
  }

  onMount(() => {
    mounted = true;
  });
</script>

<div 
  class='viewport'
  bind:this={viewport}
  bind:offsetHeight={viewport_height}
  onscroll={handle_scroll}
  style="height: {height};"
>
  <div 
    class='contents'
    bind:this={contents}
    style="padding-top: {top}px; padding-bottom: {bottom}px;"
  >
    {#each visible as row (row.index)}
      <div 
        class='row'
        bind:this={rows[row.index]}
        style="height: {item_height}px;"
        data-index={row.index}
      >
		    {@render children?.(row.data)}
      </div>
    {/each}
  </div>
</div>

<style lang="postcss">
  .viewport {
    position: relative;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    display: block;
  }

  .contents,
  .row {
    display: block;
  }

  .row {
    overflow: hidden;
  }
</style>